import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { useStore } from '@nanostores/react';
import classNames from 'classnames';

import type {
  AssetsSegmentSchemaPatched,
  AssetsSegmentSchemaWithIdPatched,
} from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  type AssetsHttpPostAssetsByAssetIdSegmentsHeader,
  type AssetsSegmentSchemaWithId,
  httpPatchAssetsByAssetIdSegmentsBySegmentId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  Button,
  Chip,
  Focusable,
  Icon,
  type PressEvent,
  type SuggestionsPluginItem,
  Tooltip,
  TooltipTrigger,
  useHover,
} from '@iconik-io/uad';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { segmentTrackEvent } from '../../../../redux/analytics/thunks';
import {
  sendCommentEdit,
  sendNewComment,
} from '../../../../redux/comment_attachments/thunks';
import { selectAssetOrSearchItem } from '../../../../redux/entities/selectors/assets';
import { selectClosedProxyByVersionId } from '../../../../redux/entities/selectors/proxies';
import type { AssetsAssetVersionSchemaWithIdExtended } from '../../../../redux/entities/selectors/versions';
import { useAppDispatch, useAppSelector } from '../../../../redux/redux_hooks';
import { serializeUnifiedAnnotation } from '../../../../utils/annotation/serialize_unified_annotation';
import { getProxyMediaType } from '../../../../utils/get_proxy_media_type';
import type { TimecodeOptions } from '../../../../utils/get_proxy_timecode_options';
import { seekableMediaTypes } from '../../../../utils/media_type';
import type { CommentAttachmentPreviewItem } from '../comment_attachments/comment_attachment_preview/comment_attachment_preview';
import { CommentAttachmentTiles } from '../comment_attachments/comment_attachment_tiles/comment_attachment_tiles';
import { usePendingAttachments } from '../comment_attachments/hooks/use_pending_attachments';
import { useSegmentAttachments } from '../comment_attachments/hooks/use_segment_attachments';
import { getAttachmentTileItem } from '../comment_attachments/utils/get_comment_form_attachments';
import { getPendingFormAttachments } from '../comment_attachments/utils/get_pending_form_attachments';
import { CommentForm } from '../comment_form/comment_form';
import {
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
} from '../comment_form/constants';
import { SegmentKeyframe } from '../segment_keyframe/segment_keyframe';
import { SegmentValue } from '../segment_value/segment_value';
import { SegmentColorMarker } from './segment_color_marker';
import { SegmentControls } from './segment_controls';
import { SegmentHeader } from './segment_header';
import { SegmentRange } from './segment_range';

import styles from './segment.module.css';

const allowedColorSegmentSet = new Set([
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
]);

type Props = {
  assetId: string;
  className?: string;
  isCurrentVersion: boolean;
  onEditMode?: (segmentId: string, metadataViewId?: string | null) => void;
  onReplyClick?: () => void;
  onReplied?: (reply: AssetsSegmentSchemaWithId) => void;
  segment: AssetsSegmentSchemaWithIdPatched;
  version?: AssetsAssetVersionSchemaWithIdExtended;
  versionName?: string;
  timecodeOptions: TimecodeOptions | undefined;
  onShowRepliesThread?: () => void;
  showReplies?: boolean;
  numberOfReplies?: number;
  hasReplies?: boolean;
  shareLinkUserEmail?: string;
  canReply?: boolean;
  canEdit: boolean;
  canApproveComments?: boolean;
  canMarkCommentComplete?: boolean;
  onMarkCompleteBlocked?: () => void;
  canDelete: boolean;
  canMention: boolean;
  filterMentionResults?: (
    query: string | null,
  ) => Promise<SuggestionsPluginItem[]>;
  shouldOpenReplyForm?: boolean;
  shouldShowCommentTypeBadge?: boolean;
  isInternalUser?: boolean;
  onOpenAttachment?: (attachment: CommentAttachmentPreviewItem) => void;
};

type SubmittingEdit = { session: number; skippedSave?: () => void };

export const Segment: FC<Props> = ({
  assetId,
  isCurrentVersion,
  onEditMode,
  onReplied,
  onReplyClick,
  segment,
  version,
  versionName,
  timecodeOptions,
  onShowRepliesThread,
  showReplies,
  numberOfReplies,
  hasReplies,
  shareLinkUserEmail,
  canReply = true,
  canEdit,
  canApproveComments = true,
  canMarkCommentComplete,
  onMarkCompleteBlocked,
  canDelete,
  canMention,
  filterMentionResults,
  shouldOpenReplyForm,
  shouldShowCommentTypeBadge,
  isInternalUser,
  onOpenAttachment,
}) => {
  // const shareToken = { email: '' };
  // const route = 'route';
  const dispatch = useAppDispatch();
  const asset = useAppSelector((state) =>
    selectAssetOrSearchItem(state, assetId),
  );
  const assetProxy = useAppSelector((state) =>
    selectClosedProxyByVersionId(state, assetId, version?.id),
  );

  const keyframe = useAppSelector((state) =>
    segment.keyframe_id
      ? state.entities.keyframes[segment.keyframe_id]
      : undefined,
  );

  const isTimeBasedAsset =
    seekableMediaTypes.has(getProxyMediaType(assetProxy)) ||
    asset?.type === 'SUBCLIP';

  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const currentUnifiedSegment = useStore(
    unifiedPlayerStore.segment.$currentSegment,
  );

  const currentPlaylistItemContentType = useStore(
    unifiedPlayerStore.playlist.$currentPlaylistItem,
  )?.contentType;
  const isContextualAnnotationVisible = useStore(
    unifiedPlayerStore.segment.annotation.$isContextualAnnotationVisible,
  );

  const selectedSegmentIds = useStore(
    unifiedPlayerStore.segment.$selectedSegmentIds,
  );

  const [editSegment, setEditSegment] = useState<
    AssetsSegmentSchemaPatched | undefined
  >(undefined);
  const [replySegment, setReplySegment] = useState<
    AssetsSegmentSchemaPatched | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  // Track editSegment in a ref for auto-save on navigation
  const editSegmentRef = useRef(editSegment);
  editSegmentRef.current = editSegment;

  // Track replySegment in a ref for auto-save on navigation
  const replySegmentRef = useRef(replySegment);
  replySegmentRef.current = replySegment;
  const isSavingReplyRef = useRef(false);

  const onRepliedRef = useRef(onReplied);
  onRepliedRef.current = onReplied;

  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletedRef = useRef(false);
  const editSessionRef = useRef(0);
  const submittingEditRef = useRef<SubmittingEdit | undefined>(undefined);

  const savedAttachments = useSegmentAttachments(segment);
  const replyAttachments = usePendingAttachments();
  const editAttachments = usePendingAttachments({ savedAttachments });
  const savedAttachmentsRef = useRef(savedAttachments);
  savedAttachmentsRef.current = savedAttachments;
  const replyAttachmentsRef = useRef(replyAttachments);
  replyAttachmentsRef.current = replyAttachments;
  const editAttachmentsRef = useRef(editAttachments);
  editAttachmentsRef.current = editAttachments;

  const savePendingEdit = useCallback(() => {
    if (isDeletedRef.current) {
      return;
    }
    const pendingEdit = editSegmentRef.current;
    if (!pendingEdit) {
      return;
    }

    editSegmentRef.current = undefined;

    const personalDraftAnnotation =
      unifiedPlayerStore.segment.annotation.$personalDraftAnnotation.get();
    const drawing = serializeUnifiedAnnotation(personalDraftAnnotation);
    const pendingAttachments = editAttachmentsRef.current;
    const hasContent =
      Boolean(pendingEdit.segment_text?.trim()) ||
      Boolean(drawing) ||
      pendingAttachments.hasAttachments;

    if (hasContent) {
      // sendCommentEdit records the attachments itself once its uploads and
      // deletes are in, so the form's copies stay out of the patch.
      const { segment_type, asset_id, attachments, has_attachments, ...rest } =
        pendingEdit;
      const filteredRest = Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value !== null),
      );

      if (drawing) {
        filteredRest.drawing = drawing;
        filteredRest.has_drawing = true;
      } else if (segment.has_drawing && segment.drawing) {
        filteredRest.drawing = null;
        filteredRest.has_drawing = false;
      }

      const patchText = () =>
        dispatch(
          httpPatchAssetsByAssetIdSegmentsBySegmentId(
            segment.asset_id,
            segment.id,
            filteredRest,
            {
              index_immediately: true,
            },
          ),
        );
      const submitting = submittingEditRef.current;
      if (submitting?.session === editSessionRef.current) {
        // The Save in flight holds this edit's files; only its text is left.
        submitting.skippedSave = patchText;
      } else {
        const claim = pendingAttachments.claim();
        dispatch(
          sendCommentEdit({
            assetId: segment.asset_id,
            segmentId: segment.id,
            patch: filteredRest,
            files: claim.files.map(({ file }) => file),
            removedIds: claim.removedIds,
            savedAttachments: savedAttachmentsRef.current,
            onFailure: () => 'publish',
          }),
        ).finally(() => {
          pendingAttachments.complete(claim);
        });
      }
    } else {
      pendingAttachments.reset();
    }

    unifiedPlayerStore.segment.setCurrentSegmentEditing(false);
    unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
  }, [segment.id, segment.asset_id, segment.has_drawing, segment.drawing]);

  const savePendingReply = useCallback(() => {
    if (isDeletedRef.current) {
      return;
    }
    const pendingReply = replySegmentRef.current;
    if (!pendingReply || isSavingReplyRef.current) {
      return;
    }

    unifiedPlayerStore.segment.setCurrentSegmentReplying(false);

    const personalDraftAnnotation =
      unifiedPlayerStore.segment.annotation.$personalDraftAnnotation.get();
    const drawing = serializeUnifiedAnnotation(personalDraftAnnotation);
    const pendingAttachments = replyAttachmentsRef.current;
    const hasContent =
      Boolean(pendingReply.segment_text?.trim()) ||
      Boolean(drawing) ||
      pendingAttachments.hasAttachments;
    if (!hasContent) {
      return;
    }

    replySegmentRef.current = undefined;
    isSavingReplyRef.current = true;
    const replyWithDrawing = {
      ...pendingReply,
      ...(drawing ? { drawing, has_drawing: true } : {}),
    };
    const headers: AssetsHttpPostAssetsByAssetIdSegmentsHeader = {};
    if (shareLinkUserEmail) {
      headers['Share-User-Email'] = shareLinkUserEmail;
    }
    if (drawing) {
      unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
    }
    const claim = pendingAttachments.claim();
    dispatch(
      sendNewComment({
        assetId: segment.asset_id,
        segment: replyWithDrawing,
        files: claim.files.map(({ file }) => file),
        headers,
        onFailure: () => 'publish',
      }),
    )
      .then((savedReply) => {
        if (!savedReply) {
          return;
        }
        onRepliedRef.current?.(savedReply);
        dispatch(
          segmentTrackEvent({
            eventName: 'Segment Created',
            properties: {
              'Media Type': currentPlaylistItemContentType,
              'Segment Type': 'Comment',
              'Segment Color': savedReply.segment_color,
              'Annotation Tool Used': Boolean(drawing),
              'Has Timecodes': false,
              'Is Reply': true,
            },
          }),
        );
      })
      .finally(() => {
        pendingAttachments.complete(claim);
        isSavingReplyRef.current = false;
      });
  }, [shareLinkUserEmail, currentPlaylistItemContentType, segment.asset_id]);

  const currentUnifiedSegmentId = currentUnifiedSegment.id;
  const isCurrentAnnotationVisible =
    currentUnifiedSegmentId === segment.id && isContextualAnnotationVisible;

  const handleSegmentClick = useCallback(() => {
    unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
  }, [segment.id]);

  const handleEdit = useCallback(() => {
    if (!isCurrentVersion) {
      return;
    }
    if (currentUnifiedSegmentId !== segment.id) {
      unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
    }
    unifiedPlayerStore.segment.setCurrentSegmentEditing(true);
    editSessionRef.current += 1;
    editAttachments.reset();
    replyAttachments.reset();
    setEditSegment(segment);
    setReplySegment(undefined);
    onEditMode?.(segment.id, segment.metadata_view_id);
  }, [
    isCurrentVersion,
    segment,
    onEditMode,
    currentUnifiedSegmentId,
    editAttachments.reset,
    replyAttachments.reset,
  ]);

  useEffect(() => {
    if (currentUnifiedSegmentId !== segment.id) {
      savePendingEdit();
      savePendingReply();

      setEditSegment(undefined);
      setReplySegment(undefined);
      unifiedPlayerStore.segment.annotation.setIsContextualAnnotationVisible(
        true,
      );
    }
  }, [currentUnifiedSegmentId, segment.id, savePendingEdit, savePendingReply]);

  const savePendingEditRef = useRef(savePendingEdit);
  const savePendingReplyRef = useRef(savePendingReply);

  useEffect(() => {
    savePendingEditRef.current = savePendingEdit;
  }, [savePendingEdit]);

  useEffect(() => {
    savePendingReplyRef.current = savePendingReply;
  }, [savePendingReply]);

  useEffect(() => {
    const unsubscribe = unifiedPlayerStore.segment.$currentSegment.listen(
      (currentSegment, previousSegment) => {
        if (
          previousSegment.id !== segment.id ||
          currentSegment.id === segment.id ||
          unifiedPlayerStore.sync.syncClient.isSyncing
        ) {
          return;
        }
        savePendingEditRef.current();
        savePendingReplyRef.current();
        setEditSegment(undefined);
        setReplySegment(undefined);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [segment.id]);

  useEffect(() => {
    return () => {
      savePendingEditRef.current();
      savePendingReplyRef.current();
      // No render follows an unmount, so a reply Send still in flight would
      // otherwise read the form as open and abort.
      replySegmentRef.current = undefined;
    };
  }, []);

  const handleDelete = useCallback(() => {
    isDeletedRef.current = true;
    setIsDeleting(true);
    unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
    dispatch(
      httpPatchAssetsByAssetIdSegmentsBySegmentId(
        segment.asset_id,
        segment.id,
        {
          status: 'DELETED',
        },
        {
          index_immediately: true,
        },
      ),
    ).finally(() => {
      setIsDeleting(false);
    });
  }, [segment.id, segment.asset_id]);

  const handleReplyClick = useCallback(() => {
    handleSegmentClick();
    editAttachments.reset();
    replyAttachments.reset();
    setEditSegment(undefined);
    if (shouldOpenReplyForm) {
      setReplySegment({
        segment_type: segment.segment_type,
        asset_id: segment.asset_id,
        version_id: segment.version_id,
        parent_id: segment.id,
        is_internal: segment.is_internal,
      });
      unifiedPlayerStore.segment.setCurrentSegmentReplying(true);
    }

    onReplyClick?.();
  }, [
    segment,
    handleSegmentClick,
    shouldOpenReplyForm,
    onReplyClick,
    editAttachments.reset,
    replyAttachments.reset,
  ]);

  // The comment editor captures this callback when its ProseMirror view is
  // created, so it must merge into the latest state, not a closed-over snapshot.
  const handleEditChange = useCallback(
    (data: Partial<AssetsSegmentSchemaPatched>) => {
      setEditSegment((previous) =>
        previous ? { ...previous, ...data } : previous,
      );
    },
    [],
  );

  const handleEditSubmit = useCallback(
    async (data: AssetsSegmentSchemaPatched) => {
      setLoading(true);
      const submitting: SubmittingEdit = { session: editSessionRef.current };
      submittingEditRef.current = submitting;

      try {
        const { segment_type, asset_id, ...rest } = data;
        // Clear the stored drawing so the annotation store owns it from here on,
        // but only once we hold it: it is fetched lazily, and nulling one we
        // never loaded would delete it.
        const clearsDrawing = Boolean(segment.drawing);
        const claim = editAttachments.claim();
        const isFormGone = () =>
          Boolean(submitting.skippedSave) ||
          editSessionRef.current !== submitting.session;
        let keptFiles: File[] = [];
        try {
          await dispatch(
            sendCommentEdit({
              assetId: segment.asset_id,
              segmentId: segment.id,
              patch: {
                ...(clearsDrawing ? { drawing: null, has_drawing: false } : {}),
                ...rest,
              },
              files: claim.files.map(({ file }) => file),
              removedIds: claim.removedIds,
              savedAttachments: savedAttachmentsRef.current,
              onFailure: () => (isFormGone() ? 'publish' : 'abort'),
              onFilesKept: (files) => {
                keptFiles = files;
              },
            }),
          );
        } catch (error) {
          if (isFormGone()) {
            editAttachments.complete(claim);
          } else {
            editAttachments.release(claim);
            for (const pending of claim.files) {
              if (keptFiles.includes(pending.file)) {
                editAttachments.removeFile(pending.localId);
              }
            }
          }
          throw error;
        }
        editAttachments.complete(claim);
        if (editSessionRef.current === submitting.session) {
          if (
            unifiedPlayerStore.segment.$currentSegment.get().id === segment.id
          ) {
            unifiedPlayerStore.segment.setCurrentSegmentEditing(false);
            unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
          }
          setEditSegment(undefined);
        }
      } catch (error) {
        if (editSessionRef.current === submitting.session) {
          submitting.skippedSave?.();
        }
        throw error;
      } finally {
        if (submittingEditRef.current === submitting) {
          submittingEditRef.current = undefined;
        }
        setLoading(false);
      }
    },
    [
      segment,
      editAttachments.claim,
      editAttachments.complete,
      editAttachments.release,
      editAttachments.removeFile,
    ],
  );

  const handleReplyChange = useCallback(
    (data: Partial<AssetsSegmentSchemaPatched>) => {
      setReplySegment((previous) =>
        previous ? { ...previous, ...data } : previous,
      );
    },
    [],
  );

  const handleReplySubmit = useCallback(
    async (data: AssetsSegmentSchemaPatched) => {
      setLoading(true);
      // Blocks savePendingReply while this send is in flight.
      isSavingReplyRef.current = true;

      const headers: AssetsHttpPostAssetsByAssetIdSegmentsHeader = {};
      if (shareLinkUserEmail) {
        headers['Share-User-Email'] = shareLinkUserEmail;
      }
      const claim = replyAttachments.claim();
      try {
        const reply = await dispatch(
          sendNewComment({
            assetId: segment.asset_id,
            segment: { ...replySegment, ...data },
            files: claim.files.map(({ file }) => file),
            headers,
            onFailure: () => (replySegmentRef.current ? 'abort' : 'publish'),
          }),
        );
        replyAttachments.complete(claim);
        setReplySegment(undefined);
        const isStillCurrent =
          unifiedPlayerStore.segment.$currentSegment.get().id === segment.id;
        if (isStillCurrent) {
          unifiedPlayerStore.segment.setCurrentSegmentReplying(false);
        }
        if (!reply) {
          return;
        }
        onReplied?.(reply);
        if (isStillCurrent) {
          unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
        }

        dispatch(
          segmentTrackEvent({
            eventName: 'Segment Created',
            properties: {
              'Media Type': currentPlaylistItemContentType,
              'Segment Type': 'Comment',
              'Segment Color': reply.segment_color,
              'Annotation Tool Used': Boolean(reply.drawing),
              'Has Timecodes': false, // We don't set timecodes for replies.
              'Is Reply': true,
            },
          }),
        );
      } catch (error) {
        if (replySegmentRef.current) {
          replyAttachments.release(claim);
        } else {
          replyAttachments.complete(claim);
        }
        throw error;
      } finally {
        isSavingReplyRef.current = false;
        setLoading(false);
      }
    },
    [
      replySegment,
      onReplied,
      shareLinkUserEmail,
      currentPlaylistItemContentType,
      segment.asset_id,
      segment.id,
      replyAttachments.claim,
      replyAttachments.complete,
      replyAttachments.release,
    ],
  );

  const handleEditCancel = useCallback(() => {
    editAttachments.reset();
    setEditSegment(undefined);
    unifiedPlayerStore.segment.setCurrentSegmentEditing(false);
    unifiedPlayerStore.segment.annotation.clearPersonalDraftAnnotation();
  }, [editAttachments.reset]);

  const handleReplyCancel = useCallback(() => {
    replyAttachments.reset();
    setReplySegment(undefined);
    unifiedPlayerStore.segment.setCurrentSegmentReplying(false);
  }, [replyAttachments.reset]);

  const handleOpenAttachment = useCallback(
    (item: { id: string; name: string; url?: string | null }) => {
      onOpenAttachment?.({
        ...item,
        assetId: segment.asset_id,
        segmentId: segment.id,
      });
    },
    [onOpenAttachment, segment.asset_id, segment.id],
  );

  const handleAnnotationButtonPress = useCallback(
    (event: PressEvent) => {
      event.continuePropagation();
      unifiedPlayerStore.segment.annotation.setIsContextualAnnotationVisible(
        !isCurrentAnnotationVisible,
      );
    },
    [isCurrentAnnotationVisible],
  );

  const { hoverProps, isHovered } = useHover({});

  const isNested = segment.parent_id;
  const showRange =
    !isNested && (isTimeBasedAsset || currentPlaylistItemContentType === 'pdf');

  const isParentSegment = segment.asset_id !== assetId;
  if (segment.status === 'DELETED') {
    return (
      <div
        className={classNames(styles.segment_wrapper, styles.deleted, {
          [styles.hovered]: isHovered,
          [styles.not_current_version]:
            !isCurrentVersion && asset?.type !== 'SUBCLIP',
        })}
        role="option"
        aria-label="Segment item"
        tabIndex={0}
        {...hoverProps}
      >
        <div className={styles.segment}>
          <SegmentColorMarker />
          {isNested ? (
            <Icon className={styles.child_icon} name="child" />
          ) : null}
          <div className={styles.segment_content}>
            <div className={styles.comment_text}>
              {showRange ? (
                <SegmentRange
                  timecodeOptions={timecodeOptions}
                  segment={segment}
                  mode={
                    currentPlaylistItemContentType === 'pdf' ? 'page' : 'smpte'
                  }
                />
              ) : null}
              Comment deleted
            </div>
          </div>
        </div>
        {!replySegment ? (
          <div className={styles.reply_form_wrapper}>
            <SegmentControls
              onReplyClick={handleReplyClick}
              segment={segment}
              replyButtonDisabled={isParentSegment || !canReply}
              showReplies={showReplies}
              onShowRepliesThread={onShowRepliesThread}
              numberOfReplies={numberOfReplies}
              hasReplies={hasReplies}
              isCurrentVersion={isCurrentVersion}
            />
          </div>
        ) : null}
      </div>
    );
  }

  const commentTypeBadge = segment.is_internal ? (
    <TooltipTrigger>
      <Focusable>
        <span>
          <Chip color="inverted" icon={<Icon size="xs" name="user_group" />}>
            Internal
          </Chip>
        </span>
      </Focusable>
      <Tooltip>Internal comment</Tooltip>
    </TooltipTrigger>
  ) : (
    <TooltipTrigger>
      <Focusable>
        <span>
          <Icon className={styles.comment_type_icon} name="globe" />
        </span>
      </Focusable>
      <Tooltip>Public comment</Tooltip>
    </TooltipTrigger>
  );

  const isSelected = selectedSegmentIds.includes(segment.id);
  return (
    <div
      onClick={handleSegmentClick}
      className={classNames(styles.segment_wrapper, {
        [styles.edit_active]: Boolean(editSegment),
        [styles.reply_active]: Boolean(replySegment),
        [styles.internal]: Boolean(segment.is_internal),
        [styles.highlighted]: isSelected,
        [styles.hovered]: isHovered,
        [styles.not_current_version]:
          !isCurrentVersion && asset?.type !== 'SUBCLIP',
      })}
      role="option"
      aria-label="Segment item"
      aria-selected={isSelected}
      tabIndex={0}
      {...hoverProps}
    >
      <div className={styles.segment}>
        <SegmentColorMarker segment={editSegment ?? segment} />
        {isNested ? <Icon className={styles.child_icon} name="child" /> : null}
        <div className={styles.segment_content}>
          <SegmentHeader
            segment={segment}
            versionName={versionName}
            onEdit={handleEdit}
            editButtonDisabled={isParentSegment || isDeleting}
            assetId={assetId}
            onDelete={handleDelete}
            canDelete={canDelete}
            isEditActive={Boolean(editSegment)}
            showActions={isHovered}
            canEdit={canEdit}
            canApproveComments={canApproveComments}
            canMarkCommentComplete={canMarkCommentComplete}
            onMarkCompleteBlocked={onMarkCompleteBlocked}
          />
          {editSegment ? (
            <CommentForm
              segment={editSegment}
              withColorsPicker={
                allowedColorSegmentSet.has(segment.segment_type) &&
                segment.top_level
              }
              isInternalUser={isInternalUser && !segment.parent_id}
              originalIsInternal={segment.is_internal}
              replyCount={numberOfReplies}
              onSubmit={handleEditSubmit}
              onChange={handleEditChange}
              onCancel={handleEditCancel}
              disabled={loading || isDeleting}
              attachments={getPendingFormAttachments(editAttachments)}
              isCompact
              canMention={canMention}
              filterMentionResults={filterMentionResults}
            />
          ) : (
            <>
              <div className={styles.comment_text}>
                {showRange ? (
                  <SegmentRange
                    timecodeOptions={timecodeOptions}
                    segment={segment}
                    mode={
                      currentPlaylistItemContentType === 'pdf'
                        ? 'page'
                        : 'smpte'
                    }
                  />
                ) : null}
                {segment.has_drawing ? (
                  <TooltipTrigger>
                    <Button
                      className={classNames({
                        [styles.annotation_button_background]:
                          isCurrentAnnotationVisible,
                      })}
                      aria-label="Annotation toggle button"
                      aria-pressed={isCurrentAnnotationVisible}
                      icon={<Icon name="annotation_annotation" />}
                      variant="tertiary"
                      size="s"
                      onPress={handleAnnotationButtonPress}
                    />
                    <Tooltip>
                      {isCurrentAnnotationVisible
                        ? 'Hide annotation'
                        : 'Show annotation'}
                    </Tooltip>
                  </TooltipTrigger>
                ) : null}
                <SegmentValue segment={segment} />
              </div>
              {savedAttachments.length > 0 ? (
                <CommentAttachmentTiles
                  items={savedAttachments.map(getAttachmentTileItem)}
                  onOpen={onOpenAttachment ? handleOpenAttachment : undefined}
                />
              ) : null}
              {keyframe && keyframe.url ? (
                <div>
                  <SegmentKeyframe url={keyframe.url} />
                </div>
              ) : null}
              <div className={styles.segment_controls}>
                {!replySegment ? (
                  <SegmentControls
                    onReplyClick={handleReplyClick}
                    segment={segment}
                    replyButtonDisabled={isParentSegment || !canReply}
                    showReplies={showReplies}
                    onShowRepliesThread={onShowRepliesThread}
                    numberOfReplies={numberOfReplies}
                    hasReplies={hasReplies}
                    isCurrentVersion={isCurrentVersion}
                  />
                ) : null}
                {shouldShowCommentTypeBadge ? commentTypeBadge : null}
              </div>
            </>
          )}
        </div>
        {/* Dummy to maintain even gaps within flex (color bar | content | dummy) */}
        <div />
      </div>
      {replySegment ? (
        <div className={styles.reply_form_wrapper}>
          <CommentForm
            className={styles.reply_form}
            inputClassName={styles.reply_input}
            segment={replySegment}
            onSubmit={handleReplySubmit}
            onChange={handleReplyChange}
            onCancel={handleReplyCancel}
            submitButton={({ isDisabled }) => (
              <Button
                size="s"
                variant="primary"
                type="submit"
                icon={<Icon name="send" />}
                isPending={loading}
                isDisabled={isDisabled}
              />
            )}
            disabled={loading}
            attachments={getPendingFormAttachments(replyAttachments)}
            isCompact
            inputPlaceholder="Add your reply here"
            autoFocus
            canMention={canMention}
            filterMentionResults={filterMentionResults}
            commentVersion={segment.version_id}
          />
        </div>
      ) : null}
    </div>
  );
};
